import React from "react";
import { ClipboardList, Github, BookOpen, Zap } from "lucide-react";

const Guidelines: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="bg-orange-500 rounded-full p-4">
                <ClipboardList className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Submission Guidelines
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Got something interesting to share — whether it's yours or
              something you discovered? Great.
            </p>
            <div className="mt-8 max-w-2xl mx-auto text-lg text-muted-foreground space-y-4">
              <p>
                Whether you built it, forked it, or just stumbled across an
                inspiring repo, we'd love to see it. The goal is simple: surface
                projects that spark ideas and show what's possible with
                Infrastructure as Agents (IaA).
              </p>
              <p className="font-semibold text-slate-900 dark:text-slate-100">
                You don't need a form or a long write-up.
                <br />
                Just submit a GitHub link.
                <br />
                That's it.
              </p>
              <p>If your README tells a clear story, you're all set.</p>
            </div>
          </div>

          {/* What Makes a Great README */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <BookOpen className="h-8 w-8 text-orange-500 mr-3" />
              What Makes a Great README
            </h2>
            <div className="grid gap-6">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-6">
                  Your README is the front door to your project — make it easy
                  for others to understand, explore, and build on. Here's what
                  helps make it effective:
                </p>
                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Clear Title and Tagline</strong> – A name that
                      quickly communicates what your agent does.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Purpose and Problem</strong> – Explain what
                      challenge the project addresses and why it matters.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>How It Works</strong> – A concise walkthrough of
                      how it's built or what powers it.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Getting Started</strong> – Simple setup and usage
                      steps so people can try it quickly.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Tech and Tools</strong> – Mention the main stack
                      or platforms (AWS, Kubernetes, Terraform, etc.).
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Examples and Demos</strong> – Show what it looks
                      like in action with samples or use cases.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>License</strong> – Include an open-source license
                      so others know how they can use or contribute.
                    </span>
                  </li>
                </ul>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mt-6 italic">
                  Think of your README as a conversation starter — a way to
                  invite others into your experiment.
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
                <h3 className="text-xl font-semibold mb-4 text-slate-900 dark:text-slate-100 flex items-center">
                  <Github className="h-5 w-5 text-blue-500 mr-2" />
                  Submissions in Any Form
                </h3>
                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Your Own Projects</strong> – Finished, prototype,
                      or early idea — all are welcome.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Interesting Finds</strong> – If you come across a
                      project that fits the InfrAgents spirit, share it.
                      Attribution is all we ask.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Collaborations</strong> – Working together or
                      building on someone else's idea is encouraged.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
                <h3 className="text-xl font-semibold mb-4 text-slate-900 dark:text-slate-100 flex items-center">
                  <Zap className="h-5 w-5 text-orange-500 mr-2" />
                  Review Process
                </h3>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                  All submissions go through a fully automated review. There are
                  no manual approvals, just straightforward checks to ensure
                  clarity, quality, and relevance.
                </p>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                  The automated system reviews:
                </p>
                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>README Content</strong> – Is it clear,
                      descriptive, and easy to understand?
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Relevance</strong> – Does it relate to
                      infrastructure, automation, or agents?
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    <span>
                      <strong>Clarity</strong> – Can others quickly grasp what
                      the project does and how?
                    </span>
                  </li>
                </ul>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mt-4">
                  If your submission is rejected, please don't be discouraged —
                  automation isn't perfect. If you believe your project should
                  be listed, simply get in touch or open an issue on the GitHub
                  project, and we'll take another look.
                </p>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 text-white">
              <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                Ready to Share Your Project?
              </h2>
              <p className="text-lg mb-6 text-orange-100">
                Just submit a GitHub link and let your README do the talking
              </p>
              <a
                href="/submit"
                className="inline-flex items-center justify-center bg-white text-orange-600 px-8 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
              >
                Submit GitHub Link
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Guidelines;
